/**
 * Shared contracts for future supplier integrations.
 *
 * This module describes the boundary between a provider account and the
 * canonical POS purchase-order/DDT services.  It deliberately does not model
 * a connector, transport, sender or stock operation.  Manual documents use
 * the same document shape but have no fabricated external identity.
 */

import { z } from 'zod';

export const SUPPLIER_INTEGRATION_CAPABILITIES = [
  'purchase_orders.read',
  'purchase_orders.send',
  'purchase_orders.confirmations',
  'ddt_passivi.read',
] as const;

export const supplierIntegrationCapabilitySchema = z.enum(
  SUPPLIER_INTEGRATION_CAPABILITIES,
);
export type SupplierIntegrationCapability = z.infer<
  typeof supplierIntegrationCapabilitySchema
>;

export const supplierDocumentKindSchema = z.enum([
  'purchase_order',
  'ddt_passivo',
]);
export type SupplierDocumentKind = z.infer<typeof supplierDocumentKindSchema>;

export const supplierDocumentSourceSchema = z.enum(['api', 'manual']);
export type SupplierDocumentSource = z.infer<
  typeof supplierDocumentSourceSchema
>;

const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/i, 'SHA-256 non valido');
const externalIdSchema = z.string().trim().min(1).max(255);
const externalRevisionSchema = z.string().trim().min(1).max(128);

/**
 * External identity is intentionally separate from a supplier document
 * number.  A number can be reused by different accounts/suppliers and is
 * therefore only descriptive data.
 */
export const supplierExternalDocumentIdentitySchema = z
  .object({
    account_id: externalIdSchema,
    supplier_id: externalIdSchema,
    document_id: externalIdSchema,
  })
  .strict();
export type SupplierExternalDocumentIdentity = z.infer<
  typeof supplierExternalDocumentIdentitySchema
>;

export const supplierExternalLineIdentitySchema = z
  .object({
    document: supplierExternalDocumentIdentitySchema,
    line_id: externalIdSchema,
  })
  .strict();
export type SupplierExternalLineIdentity = z.infer<
  typeof supplierExternalLineIdentitySchema
>;

export const supplierDocumentRevisionSchema = z
  .object({
    revision: externalRevisionSchema,
    /**
     * Provider supplied content hash, when available.  The ingestion service
     * also computes a canonical hash, which is the value used for replay
     * decisions.
     */
    content_hash: sha256Schema.optional(),
  })
  .strict();
export type SupplierDocumentRevision = z.infer<
  typeof supplierDocumentRevisionSchema
>;

export const supplierOriginalArtifactSchema = z
  .object({
    /** Relative key/path managed by the existing storage boundary. */
    storage_path: z.string().trim().min(1).max(2048),
    mime_type: z.string().trim().min(1).max(255).optional(),
    sha256: sha256Schema.optional(),
  })
  .strict();
export type SupplierOriginalArtifact = z.infer<
  typeof supplierOriginalArtifactSchema
>;

export const supplierPurchaseOrderReferenceSchema = z
  .object({
    external_document_id: externalIdSchema.optional(),
    document_number: z.string().trim().min(1).max(255).optional(),
  })
  .strict()
  .refine(
    (reference) =>
      reference.external_document_id !== undefined ||
      reference.document_number !== undefined,
    { message: 'Il riferimento ordine deve avere un id o un numero esterno' },
  );
export type SupplierPurchaseOrderReference = z.infer<
  typeof supplierPurchaseOrderReferenceSchema
>;

/**
 * Supplier line values are document values.  `unit_price: null` means that
 * the provider did not state a price; it is never converted to a stock cost.
 * Product resolution happens server-side through pos_supplier_product_skus.
 */
export const supplierDocumentLineSchema = z
  .object({
    external_line_id: externalIdSchema.optional(),
    product_id: z.string().uuid().optional(),
    variant_id: z.string().uuid().nullable().optional(),
    supplier_sku: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2000).optional(),
    quantity: z.coerce.number().positive(),
    unit_of_measure: z.string().trim().min(1).max(20).optional(),
    unit_price: z.coerce.number().nonnegative().nullable().optional(),
    discount_percent: z.coerce.number().min(0).max(100).optional(),
    vat_rate: z.coerce.number().min(0).max(100).optional(),
    vat_nature: z.string().trim().min(1).max(10).nullable().optional(),
    lot_number: z.string().trim().max(100).nullable().optional(),
    notes: z.string().max(20000).nullable().optional(),
  })
  .strict();
export type SupplierDocumentLine = z.infer<typeof supplierDocumentLineSchema>;

const supplierDocumentFields = {
  kind: supplierDocumentKindSchema,
  document_number: z.string().trim().min(1).max(255).optional(),
  document_date: z.string().trim().min(1).max(64).optional(),
  lines: z.array(supplierDocumentLineSchema).max(10_000),
  purchase_order_reference: supplierPurchaseOrderReferenceSchema.optional(),
  original: supplierOriginalArtifactSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
} as const;

const supplierApiDocumentSchema = z
  .object({
    source: z.literal('api'),
    ...supplierDocumentFields,
    identity: supplierExternalDocumentIdentitySchema,
    revision: supplierDocumentRevisionSchema,
  })
  .strict();

const supplierManualDocumentSchema = z
  .object({
    source: z.literal('manual'),
    ...supplierDocumentFields,
    identity: z.undefined().optional(),
    revision: z.undefined().optional(),
  })
  .strict();

export const supplierDocumentIngestionSchema = z
  .union([supplierApiDocumentSchema, supplierManualDocumentSchema])
  .superRefine((document, context) => {
    if (document.source !== 'api') return;
    document.lines.forEach((line, index) => {
      if (!line.external_line_id) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['lines', index, 'external_line_id'],
          message: 'Le righe API devono avere un identificativo esterno stabile',
        });
      }
    });
  });
export type SupplierDocumentIngestionInput = z.infer<
  typeof supplierDocumentIngestionSchema
>;
export type SupplierApiDocumentIngestionInput = z.infer<
  typeof supplierApiDocumentSchema
>;
export type SupplierManualDocumentIngestionInput = z.infer<
  typeof supplierManualDocumentSchema
>;

/**
 * This is trusted server-side source context, not a client payload.  The
 * service still verifies each id against the tenant and resolves the company
 * from the warehouse before calling a canonical document service.
 */
export interface SupplierIngestionAuthorizationContext {
  tenant_id: string;
  company_id: string;
  warehouse_id: string;
  supplier_id: string;
  account_id: string;
  supplier_external_id?: string;
  capabilities?: readonly SupplierIntegrationCapability[];
  actor_id?: string;
  actor_role?: string;
}

export const supplierCapabilitySetSchema = z
  .object({
    account_id: externalIdSchema,
    capabilities: z.array(supplierIntegrationCapabilitySchema),
  })
  .strict();
export type SupplierCapabilitySet = z.infer<
  typeof supplierCapabilitySetSchema
>;

export const supplierDocumentIngestionStatusSchema = z.enum([
  'created',
  'replayed',
  'updated',
]);
export type SupplierDocumentIngestionStatus = z.infer<
  typeof supplierDocumentIngestionStatusSchema
>;

export interface SupplierDocumentIngestionResult {
  status: SupplierDocumentIngestionStatus;
  kind: SupplierDocumentKind;
  external_document_id: string | null;
  internal_document_id: string;
  payload_hash: string;
  revision: string | null;
  unresolved_purchase_order_reference: SupplierPurchaseOrderReference | null;
  internal_line_ids: string[];
}

export const SUPPLIER_INTEGRATION_ERROR_CODES = [
  'supplier_context_invalid',
  'supplier_capability_not_enabled',
  'supplier_external_identity_conflict',
  'supplier_revision_conflict',
  'supplier_revision_not_safe',
  'supplier_product_mapping_missing',
  'supplier_product_mapping_ambiguous',
  'supplier_product_mapping_mismatch',
  'supplier_vat_invalid',
  'supplier_unit_of_measure_incompatible',
  'supplier_purchase_order_reference_unresolved',
] as const;
export type SupplierIntegrationErrorCode = (typeof SUPPLIER_INTEGRATION_ERROR_CODES)[number];